import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { adminLogin, adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminListOrders } from "../lib/api";
import { toast } from "sonner";
import { formatAUD } from "../data/pricing";
import { CATEGORIES } from "../data/pricing";
import { Trash2, Pencil, Plus, LogOut } from "lucide-react";
import { stickerDataUrl } from "../lib/stickerPreview";

const CAT_OPTIONS = CATEGORIES.filter((c) => c !== "All");

export default function Admin() {
  const [pw, setPw] = useState("");
  const [auth, setAuth] = useState(() => localStorage.getItem("sf_admin_pw") || "");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", category: "Thank You", color: "#FF5A36", textColor: "#FFFFFF", design_style: "pill" });

  const login = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(pw);
      setAuth(pw);
      localStorage.setItem("sf_admin_pw", pw);
      toast.success("Welcome, admin");
    } catch { toast.error("Invalid password"); }
  };
  const logout = () => { setAuth(""); localStorage.removeItem("sf_admin_pw"); };

  const refresh = async () => {
    try {
      const [p, o] = await Promise.all([adminListProducts(auth), adminListOrders(auth)]);
      setProducts(p); setOrders(o);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); toast.error("Session expired"); }
    }
  };
  useEffect(() => { if (auth) refresh(); }, [auth]);

  const resetForm = () => { setEditing(null); setForm({ name: "", description: "", category: "Thank You", color: "#FF5A36", textColor: "#FFFFFF", design_style: "pill" }); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await adminUpdateProduct(auth, editing, form);
      else await adminCreateProduct(auth, form);
      toast.success("Saved");
      resetForm();
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const edit = (p) => { setEditing(p.id); setForm({ name: p.name, description: p.description, category: p.category, color: p.color, textColor: p.textColor, design_style: p.design_style }); };
  const del = async (id) => { if (!confirm("Delete this design?")) return; await adminDeleteProduct(auth, id); toast.success("Deleted"); refresh(); };

  if (!auth) {
    return (
      <div className="App">
        <Header />
        <section className="max-w-md mx-auto px-5 pt-16">
          <div className="sf-card p-8">
            <div className="eyebrow">Admin</div>
            <h1 className="font-display text-3xl font-black mt-2">Sign in</h1>
            <form onSubmit={login} className="mt-6 space-y-3">
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password"
                className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white" data-testid="admin-password-input" />
              <button className="btn-primary w-full" data-testid="admin-login-btn">Sign in</button>
            </form>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">Admin</div>
            <h1 className="font-display text-3xl font-black mt-2">Dashboard</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setTab("products")} className={`chip ${tab === "products" ? "chip-active" : ""}`} data-testid="admin-tab-products">Products ({products.length})</button>
            <button onClick={() => setTab("orders")} className={`chip ${tab === "orders" ? "chip-active" : ""}`} data-testid="admin-tab-orders">Orders ({orders.length})</button>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={16}/> Sign out</button>
          </div>
        </div>

        {tab === "products" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
            <form onSubmit={save} className="sf-card p-5 lg:col-span-4 space-y-3">
              <div className="eyebrow">{editing ? "Edit design" : "New design"}</div>
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-field-name" required/>
              <TextArea label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="admin-field-description" required/>
              <label className="block">
                <span className="eyebrow block mb-1">Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E2DDD5] bg-white" data-testid="admin-field-category">
                  {CAT_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Field label="BG Color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} testid="admin-field-color"/>
                <Field label="Text Color" value={form.textColor} onChange={(v) => setForm({ ...form, textColor: v })} testid="admin-field-textcolor"/>
              </div>
              <label className="block">
                <span className="eyebrow block mb-1">Design style</span>
                <select value={form.design_style} onChange={(e) => setForm({ ...form, design_style: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E2DDD5] bg-white" data-testid="admin-field-style">
                  <option value="pill">Pill</option>
                  <option value="circle">Circle</option>
                </select>
              </label>
              <div className="rounded-xl overflow-hidden border border-[#E2DDD5]">
                <img src={stickerDataUrl({ ...form, size: 300 })} alt="preview" className="w-full"/>
              </div>
              <div className="flex gap-2">
                <button className="btn-primary flex-1" data-testid="admin-save">{editing ? "Update" : "Create"}</button>
                {editing && <button type="button" onClick={resetForm} className="btn-outline">Cancel</button>}
              </div>
            </form>

            <div className="lg:col-span-8">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display font-bold">All designs</div>
                <button onClick={resetForm} className="btn-ghost"><Plus size={16}/> New</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {products.map((p) => (
                  <div key={p.id} className="sf-card overflow-hidden" data-testid={`admin-product-${p.slug}`}>
                    <div className="w-full aspect-square bg-[#F3EFEA] flex items-center justify-center p-3">
                      <img src={p.image || stickerDataUrl(p)} alt="" className="max-w-full max-h-full object-contain"/>
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-slate-500">{p.category}</div>
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="flex gap-1 mt-2">
                        <button onClick={() => edit(p)} className="btn-ghost text-xs" data-testid={`admin-edit-${p.slug}`}><Pencil size={14}/> Edit</button>
                        <button onClick={() => del(p.id)} className="btn-ghost text-xs text-[#E04826]" data-testid={`admin-delete-${p.slug}`}><Trash2 size={14}/></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="sf-card mt-8 p-5">
            {orders.length === 0 ? <div className="text-slate-500 text-center py-10">No orders yet.</div> :
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-xs tracking-widest">
                    <th className="py-2">Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-[#E2DDD5]" data-testid={`admin-order-${o.order_number}`}>
                      <td className="py-3 font-mono-brand">{o.order_number}</td>
                      <td>{o.first_name} {o.last_name}<div className="text-xs text-slate-500">{o.email}</div></td>
                      <td>{o.items.length}</td>
                      <td className="price-tick font-semibold">{formatAUD(o.total)}</td>
                      <td className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-AU")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
function Field({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-1">{label}</span>
      <input required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border border-[#E2DDD5] bg-white" data-testid={testid}/>
    </label>
  );
}
function TextArea({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-1">{label}</span>
      <textarea required={required} rows={2} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border border-[#E2DDD5] bg-white resize-none" data-testid={testid}/>
    </label>
  );
}
